import { guid, Guid } from "client_legacy/ndaw/guid";
import { useMainMenu } from "../useMainMenu";
import {
	MainMenuItem,
	useMainMenuStore
} from "client/Features/Layout/mainMenuStore";
import { useMemo } from "react";
import { shallowComparison } from "client/Misc/compareUtils";
import { useActivityFeedStore } from "client/Features/ActivityFeed/store";
import { AppType } from "client/Api/Apps/common";
import { EmailClient, getEffectiveEmailClient } from "client_legacy/Email/emailClient";
import { CalendarClient, getEffectiveCalendarClient } from "client/Features/Calendar/calendarClient";
import { MenuItemIconValue } from "./MenuItem";
import { useAiAssistantBranding } from "client/Features/ChatAssistant/useAiAssistantBranding";
import { toggleKellyWidget } from "client/Features/KellyWidget/KellyWidget";

export function useMenuItems(): readonly MobileMenuItem[] {
	const {
		activeMenuItem,
		setActiveMenuItem
	} = useMainMenu();

	const {
		isAddressBookAvailable,
		isDocumentsAppAvailable,
		isHubAppAvailable,
		isAiAssistantAvailable
	} = useMainMenuStore(
		x => ({
			isAddressBookAvailable: x.isAddressBookAvailable,
			isDocumentsAppAvailable: x.isDocumentsAppAvailable,
			isHubAppAvailable: x.isHubAppAvailable,
			isAiAssistantAvailable: x.isAiAssistantAvailable
		}),
		shallowComparison
	);

	const { smallAvatarUrl } = useAiAssistantBranding();

	const activityUnreadCount: number | undefined = useActivityFeedStore(state => state.amountOfUnreadNotifications);

	const items = useMemo(
		(): MobileMenuItem[] => {
			const result: MobileMenuItem[] = [
				{
					icon: {
						kind: "icon-value",
						value: "rocket"
					},
					isActive: activeMenuItem === "space",
					onClick: () => setActiveMenuItem("space"),
					uniqueKey: guid("2719dfcc-22d9-47da-887d-4a26dbc53506"),
					trackingId: "workspace-item"
				}
			];

			if (isAiAssistantAvailable) {
				result.push({
					icon: {
						kind: "ai-assistant",
						url: smallAvatarUrl
					},
					isActive: activeMenuItem === MainMenuItem.ChatAssistant,
					onClick: toggleKellyWidget,
					uniqueKey: guid("7c7688d6-7dd7-4e41-bdcf-b082f91914fb"),
					trackingId: "chat-assistant-item"
				});
			}

			result.push({
				icon: {
					kind: "icon-value",
					value: "magnifying-glass"
				},
				isActive: activeMenuItem === "search",
				onClick: () => setActiveMenuItem("search"),
				uniqueKey: guid("eb3e4aa3-21c0-4404-88b5-86d90a078337"),
				trackingId: "search-item"
			});

			if (isHubAppAvailable) {
				result.push({
					icon: {
						kind: "icon-value",
						value: "the-hub"
					},
					isActive: activeMenuItem === AppType.Hub,
					onClick: () => setActiveMenuItem(AppType.Hub),
					uniqueKey: guid("054d34d9-8790-43e0-81d0-3de7744800c7"),
					trackingId: "hub-item"
				});
			}

			result.push({
				icon: {
					kind: "icon-value",
					value: "bell"
				},
				isActive: activeMenuItem === MainMenuItem.Activity,
				onClick: () => setActiveMenuItem(MainMenuItem.Activity),
				counter: activityUnreadCount,
				uniqueKey: guid("98b407ed-7d64-49e1-81ee-743f248baa16"),
				trackingId: "activity-item"
			});

			if (isAddressBookAvailable) {
				result.push({
					icon: {
						kind: "icon-value",
						value: "address-book"
					},
					isActive: activeMenuItem === AppType.AddressBook,
					onClick: () => setActiveMenuItem(AppType.AddressBook),
					uniqueKey: guid("ddcb3bce-2438-40c1-a32b-5d238dc2e40d"),
					trackingId: "address-book"
				});
			}

			if (isDocumentsAppAvailable) {
				result.push({
					icon: {
						kind: "icon-value",
						value: "folder-open"
					},
					isActive: activeMenuItem === AppType.Documents,
					onClick: () => setActiveMenuItem(AppType.Documents),
					uniqueKey: guid("c9870dc8-1ec1-4309-8fdb-c312d7f182e2"),
					trackingId: "documents"
				});
			}

			const emailClient: EmailClient = getEffectiveEmailClient();

			if (emailClient.supportsOverview) {
				result.push({
					icon: {
						kind: "icon-value",
						value: "envelope"
					},
					isActive: activeMenuItem === AppType.Email,
					onClick: () => setActiveMenuItem(AppType.Email),
					uniqueKey: guid("125ef566-0fd5-4b34-9f6d-e112d3f28ec5"),
					trackingId: "email-item"
				});
			}

			const effectiveCalendarClient: CalendarClient = getEffectiveCalendarClient();

			if (effectiveCalendarClient.doesSupportOverview) {
				result.push({
					icon: {
						kind: "icon-value",
						value: "calendar"
					},
					isActive: activeMenuItem === AppType.Office365Calendar,
					onClick: () => setActiveMenuItem(AppType.Office365Calendar),
					uniqueKey: guid("173bb7a1-0728-4894-8e20-2251f3412149"),
					trackingId: "calendar"
				});
			}

			result.push({
				icon: {
					kind: "icon-value",
					value: "grid-2"
				},
				isActive: false,
				onClick: () => setActiveMenuItem("app-store"),
				uniqueKey: guid("d4c11ec4-4516-4c68-b61d-d8ceaf473281"),
				trackingId: "app-store"
			});

			result.push({
				icon: {
					kind: "icon-value",
					value: "gear"
				},
				isActive: activeMenuItem === "settings",
				onClick: () => setActiveMenuItem("settings"),
				uniqueKey: guid("de5b2055-dcfd-4504-a7cd-f131fac22700"),
				trackingId: "settings"
			});

			return result;
		},
		[
			activeMenuItem,
			activityUnreadCount,
			isAddressBookAvailable,
			isDocumentsAppAvailable,
			isHubAppAvailable,
			isAiAssistantAvailable,
			smallAvatarUrl,
			setActiveMenuItem
		]
	);

	return items;
};

export type MobileMenuItem = {
	icon: MenuItemIconValue;
	onClick: () => void;
	uniqueKey: Guid;
	isActive?: boolean;
	counter?: number;
	trackingId?: string;
};